import { AlertTriangle, CircleCheck, Clock, Info, OctagonAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'
import type { StatusTone } from '@/design-system/components/StatusBadge/StatusBadge'
import { alertStatusTone, type AlertStatusValue } from '@/design-system/components/StatusBadge/statusFamilies'

const icons = {
  success: CircleCheck,
  info: Info,
  warning: AlertTriangle,
  danger: OctagonAlert,
  neutral: Info,
  // `pending` (violet) ajouté le 16 sept 2026 (audit UX/UI Phase 1, §6.3) au
  // ton du design system — manquait ici, `tsc -b` (pas `tsc --noEmit` seul,
  // voir mémoire) l'a révélé en Phase 4.
  pending: Clock,
  dangerLight: Info,
  neutralLight: Info,
  neutralStrong: Info,
} satisfies Record<StatusTone, typeof Info>

export interface AlertBannerProps {
  title: string
  children: ReactNode
  /**
   * Gravité de l'alerte, famille « alerte » des statuts : un signalement
   * temporaire, jamais l'état permanent d'une entité. La page nomme la
   * gravité, le design system en tire la couleur.
   */
  severity?: AlertStatusValue
  tone?: StatusTone
  action?: ReactNode
}

export function AlertBanner({ title, children, severity, tone = 'info', action }: AlertBannerProps) {
  const resolved = severity ? alertStatusTone[severity] : tone
  const Icon = icons[resolved]

  return (
    <div
      className={mergeClassNames('ds-alert', `ds-alert--${resolved}`)}
      role={resolved === 'danger' ? 'alert' : 'status'}
    >
      <Icon className="ds-alert__icon" aria-hidden="true" />
      <div className="ds-alert__content">
        <strong>{title}</strong>
        <span>{children}</span>
      </div>
      {action ? <div className="ds-alert__action">{action}</div> : null}
    </div>
  )
}
